import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../api/client';
import { useAuth } from '../auth/AuthContext';
import { Alert, Button, Card, Field, Input } from '../ui/basics';
import { useToast } from '../ui/overlay';
import { rules, useFormValidation } from '../validation';

const SCHEMA = {
  current: rules.required('Current password'),
  next: [rules.required('New password'),
    rules.check((v) => String(v).length >= 8, 'Password must be at least 8 characters.'),
    rules.check((v) => !/^\d+$/.test(String(v)), 'Password cannot be only numbers.'),
    rules.differs('current', 'Choose a password different from the current one.')],
  confirm: [rules.required('Confirmation'), rules.matches('next', 'Passwords do not match.')],
};

export default function ChangePassword() {
  const { user, refresh, logout } = useAuth();
  const navigate = useNavigate();
  const toast = useToast();
  const forced = user.must_change_password;
  const [form, setForm] = useState({ current: '', next: '', confirm: '' });
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const set = (key) => (e) => setForm({ ...form, [key]: e.target.value });

  const v = useFormValidation(form, SCHEMA);
  const bind = (key) => ({ value: form[key], onChange: set(key), ...v.props(key) });

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    if (!v.check()) return;
    setBusy(true);
    try {
      await api.post('auth/change-password/', { current_password: form.current, new_password: form.next });
      await refresh();
      toast('Password updated', 'success');
      navigate('/', { replace: true });
    } catch (err) {
      setError(err.message);
      setBusy(false);
    }
  };

  return (
    <div style={{ maxWidth: 480, margin: forced ? '8vh auto' : '0 auto', padding: forced ? 16 : 0 }}>
      <Card title={forced ? 'Set a new password' : 'Change password'}
        subtitle={forced ? 'For security, choose your own password before continuing.' : 'You will stay signed in on this device.'}>
        <form className="stack" onSubmit={submit}>
          {forced && <Alert tone="warning">Your password was set by an administrator and must be changed now.</Alert>}
          {(v.summary || error) && <Alert>{error || v.summary}</Alert>}
          <Field label="Current password *" error={v.shown('current')}><Input type="password" autoComplete="current-password" {...bind('current')} /></Field>
          <Field label="New password *" error={v.shown('next')} hint="At least 8 characters. Avoid common or all-numeric passwords.">
            <Input type="password" autoComplete="new-password" {...bind('next')} />
          </Field>
          <Field label="Confirm new password *" error={v.shown('confirm')}>
            <Input type="password" autoComplete="new-password" {...bind('confirm')} />
          </Field>
          <div className="row">
            <Button variant="primary" type="submit" loading={busy}>Update password</Button>
            {forced ? <Button variant="ghost" onClick={logout}>Sign out</Button>
              : <Button variant="ghost" onClick={() => navigate(-1)}>Cancel</Button>}
          </div>
        </form>
      </Card>
    </div>
  );
}
